import React, { useContext } from 'react';
import {
  Button,
  Image,
  Link,
  Text,
} from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { Checkmark } from '@adsui/assets';
import { LoginErrorCard } from './login-error-card';
import { useLoginErrorPageStyles } from './styles';
import MicrosoftIcon from '../media/btn-microsoft.svg';

// Help center deep-links are locale-scoped (…/apex/ads/{locale}/…). Derive the language segment
// from the server-provided CurrentLocale (e.g. "en-US" -> "en"), defaulting to English.
const useLearnMoreUrl = (): string => {
  const serverContext = useContext(ServerContext) as { CurrentLocale?: string };
  const locale = (serverContext?.CurrentLocale || 'en-us').toLowerCase().split('-')[0];
  return `https://help.ads.microsoft.com/#apex/ads/${locale}/60043/-1`;
};

export type AadSwitchCompletePageProps = {
  // Continues into Microsoft Advertising now that the identity has been migrated to work/school.
  onContinue: () => void;
};

// Shown after the server-side MSA->AAD migration completes. Replaces the legacy server confirmation
// page: it confirms success and sends the now-migrated user into the app.
export const AadSwitchCompletePage: React.FC<AadSwitchCompletePageProps> = ({ onContinue }) => {
  const i18n = useContext(I18nContext);
  const classes = useLoginErrorPageStyles();

  return (
    <LoginErrorCard
      idPrefix="login-aad-switch-complete"
      illustration={(
        <Checkmark
          className={classes.illustration}
          role="img"
          aria-label={i18n.getString(_TL_('Success'))}
        />
      )}
      title={i18n.getString(_TL_('You\'re now using your Microsoft work or school account'))}
      actions={(
        <Button
          className="login-aad-switch-complete-continue"
          appearance="primary"
          onClick={onContinue}
        >
          {i18n.getString(_TL_('Continue to Microsoft Advertising'))}
        </Button>
      )}
    >
      <Text block>
        {i18n.getString(_TL_('Use this account when you sign in to Microsoft Advertising.'))}
      </Text>
    </LoginErrorCard>
  );
};

export type AadSignInRequiredPageProps = {
  // Starts the AAD (work/school) federated sign-in.
  onContinue: () => void;
  onGoBack: () => void;
  onLearnMore?: () => void;
};

// Shown when an already-migrated user tries to sign in with their pre-migration MSA/Google identity.
// Replaces the legacy server "user name/email address is deactivated" page, guiding the user to sign
// in with the Microsoft work or school account instead.
export const AadSignInRequiredPage: React.FC<AadSignInRequiredPageProps> = ({
  onContinue,
  onGoBack,
  onLearnMore,
}) => {
  const i18n = useContext(I18nContext);
  const learnMoreUrl = useLearnMoreUrl();

  return (
    <LoginErrorCard
      idPrefix="login-aad-signin-required"
      title={i18n.getString(_TL_('Sign in with your Microsoft work or school account'))}
      actions={(
        <>
          <Button
            className="login-aad-signin-required-continue"
            appearance="primary"
            icon={<Image src={MicrosoftIcon} width={16} height={16} alt="" aria-hidden="true" />}
            onClick={onContinue}
          >
            {i18n.getString(_TL_('Continue with Microsoft'))}
          </Button>
          <Button
            className="login-aad-signin-required-go-back"
            appearance="secondary"
            onClick={onGoBack}
          >
            {i18n.getString(_TL_('Go back'))}
          </Button>
        </>
      )}
    >
      <Text block>
        {i18n.getString(_TL_('Use this account each time you sign in to Microsoft Advertising.'))}
        {' '}
        <Link
          className="login-aad-signin-required-learn-more"
          href={learnMoreUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => onLearnMore && onLearnMore()}
          inline
        >
          {i18n.getString(_TL_('Learn more'))}
        </Link>
      </Text>
    </LoginErrorCard>
  );
};
